import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Steps } from "primereact/steps";
import { Toast } from "primereact/toast";
import { useGlobalContext } from "../../../Config";
import useClienteForm from "../hooks/useClienteForm";
import useRepresentanteForm from "../hooks/useRepresentanteForm";
import useDireccionForm from "../hooks/useDireccionForm";
import useContactoForm from "../hooks/useContactoForm";
import StepClienteForm from "./StepClienteForm";
import StepRepresentanteForm from "./StepRepresentanteForm";
import StepDireccionForm from "./StepDireccionForm";
import StepContactoForm from "./StepContactoForm";
import * as clienteService from "../../../services/ClienteService";

const FormCrearCliente = ({
  modoEdicion,
  clienteData,
  onSuccess,
  onError,
  onClose,
}) => {
  const { baseUrl, urlMedidores, urlDocumentos, urlCobranzas } =
    useGlobalContext();
  const toast = useRef();
  const [activeStep, setActiveStep] = useState(0);

  const cliente = useClienteForm(clienteData, modoEdicion, baseUrl, toast);
  const representante = useRepresentanteForm(baseUrl, toast);
  const direccion = useDireccionForm(baseUrl, toast);
  const contacto = useContactoForm(baseUrl, toast);

  useEffect(() => {
    if (clienteData) {
      representante.resetFormularioRepresentante();
      direccion.resetFormularioContacto?.();
      contacto.resetFormularioContacto();

      representante.setRepresentantesIniciales(
        clienteData.representantes_legales || clienteData.representantes
      );
      direccion.setDireccionesIniciales(clienteData.domicilios);
      contacto.setContactosIniciales(clienteData.contactos);
    }
  }, [clienteData]); // eslint-disable-line react-hooks/exhaustive-deps

  const stepsItems = [
    { label: "Cliente", command: () => setActiveStep(0) },
    { label: "Representante Legal", command: () => setActiveStep(1) },
    { label: "Dirección", command: () => setActiveStep(2) },
    { label: "Contacto", command: () => setActiveStep(3) },
  ];

  const confirmGuardar = async () => {
    if (!modoEdicion) {
      try {
        const rutLimpio = cliente.nuevoRut.replace(/[.\s]/g, "");
        const data = await clienteService.getClienteByRut(baseUrl, rutLimpio);
        if (data === true) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: "El RUT del cliente ya existe",
            life: 3000,
          });
          return;
        }
        validarCamposFaltantes();
      } catch (error) {
        console.error("Error:", error);
        toast.current.show({
          severity: "error",
          summary: "Error en la Operación",
          detail: "No se pudo validar el RUT. Por favor, intente nuevamente.",
          life: 3000,
        });
      }
    } else {
      validarCamposFaltantes();
    }
  };

  const validarCamposFaltantes = () => {
    const sinRepresentantes = representante.nuevoRepresentantes.length === 0;
    const sinDirecciones = direccion.nuevoDirecciones.length === 0;
    const sinContactos = contacto.nuevoContactos.length === 0;

    if (sinRepresentantes && sinDirecciones && sinContactos) {
      const continuar = window.confirm(
        "Atención: No se han registrado datos de contacto para este cliente.\n\n" +
          "• No hay representantes legales registrados\n" +
          "• No hay direcciones registradas\n" +
          "• No hay contactos registrados\n\n" +
          "Si ha ingresado información en estos campos, por favor presione 'Agregar' para guardarlos.\n" +
          "De lo contrario, estos campos quedarán vacíos en el registro.\n\n" +
          "¿Desea continuar con el registro?"
      );
      if (!continuar) return;
    }

    if (modoEdicion) {
      handleGuardarCliente();
    } else {
      handleCrearCliente();
    }
  };

  const handleCrearCliente = async () => {
    cliente.setLoading(true);
    let clienteCreado = false;
    const serviciosFallidos = [];
    let idCobranza = 1111;

    try {
      const missingFields = cliente.checkFormClientes();
      if (missingFields.length > 0) {
        alert(
          "Faltan completar los siguientes campos: " + missingFields.join(", ")
        );
        cliente.setLoading(false);
        return;
      }

      const rutLimpio = cliente.nuevoRut.replace(/[.\s]/g, "");
      const bodegasParams = new URLSearchParams({
        idEmpresa: cliente.nuevaEmpresa,
        idTipoCliente: cliente.nuevoTipo,
        rut: rutLimpio,
        razonSocial: cliente.nuevaRazon,
        giro: cliente.giro,
        alias: cliente.nuevoAlias,
        nombreFantasia: cliente.nuevoFanta,
        contactos: JSON.stringify(
          contacto.nuevoContactos.map((c) => ({
            nombre: c.nombre,
            tipo: c.tipo,
            telefono: c.telefono,
            correo: c.correo,
          }))
        ),
        domicilios: JSON.stringify(
          direccion.nuevoDirecciones.map((d) => ({
            direccion: d.direccion,
            tipo: d.tipo,
            comuna: d.comuna,
            region: d.region,
          }))
        ),
        representantesLegales: JSON.stringify(
          representante.nuevoRepresentantes.map((r) => ({
            rut: r.rut,
            nombre: r.nombre,
            correo: r.correo,
            telefono: r.telefono,
          }))
        ),
      });

      const serviciosNoOperativos =
        await clienteService.validarServiciosOperativos(
          urlMedidores,
          urlCobranzas,
          urlDocumentos
        );
      if (serviciosNoOperativos.length > 0) {
        toast.current.show({
          severity: "error",
          summary: "Servicios no operativos",
          detail: `No se puede crear el cliente. Servicios caídos: ${serviciosNoOperativos.join(
            ", "
          )}`,
          life: 5000,
        });
        return;
      }

      const data = await clienteService.crearCliente(baseUrl, bodegasParams);

      try {
        const cobranzasData = {
          id: data.idCliente,
          razonSocial: cliente.nuevaRazon,
          rut: rutLimpio,
        };
        const dataCobranzas = await clienteService.crearClienteCobranzas(
          urlCobranzas,
          cobranzasData
        );
        idCobranza = dataCobranzas?.id ?? 1111;
      } catch {
        serviciosFallidos.push("Cobranzas");
      }

      const updateParams = new URLSearchParams({
        idCliente: data.idCliente,
        idEmpresa: cliente.nuevaEmpresa,
        idTipoCliente: cliente.nuevoTipo,
        rut: rutLimpio,
        razonSocial: cliente.nuevaRazon,
        alias: cliente.nuevoAlias,
        nombreFantasia: cliente.nuevoFanta,
        giro: cliente.giro,
        idCobranza: idCobranza,
      });
      try {
        await clienteService.actualizarCliente(baseUrl, updateParams);
      } catch {
        serviciosFallidos.push("Bodegas (actualización)");
      }

      const documentosParams = new URLSearchParams({
        id: data.idCliente,
        rut: rutLimpio,
        razonSocial: cliente.nuevaRazon,
        giro: cliente.giro,
        direccion: direccion.nuevoDirecciones[0]?.direccion || "SIN DIRECCION",
        comuna: direccion.nuevoDirecciones[0]?.comuna || "SIN COMUNA",
        telefono: contacto.nuevoContactos[0]?.telefono || "000000000",
        idCobranza: idCobranza,
      });
      try {
        await clienteService.crearClienteDocumentos(
          urlDocumentos,
          documentosParams
        );
      } catch {
        serviciosFallidos.push("Documentos");
      }

      const medidoresParams = new URLSearchParams({
        rut: rutLimpio,
        razonSocial: cliente.nuevaRazon,
        direccion: direccion.nuevoDirecciones[0]?.direccion || "SIN DIRECCION",
        comuna: direccion.nuevoDirecciones[0]?.comuna || "SIN COMUNA",
        telefono: contacto.nuevoContactos[0]?.telefono || "000000000",
        idCobranzas: idCobranza,
      });
      try {
        await clienteService.crearClienteMedidores(
          urlMedidores,
          medidoresParams
        );
      } catch {
        serviciosFallidos.push("Medidores");
      }

      toast.current.show(
        serviciosFallidos.length > 0
          ? {
              severity: "warn",
              summary: "Creación Parcial",
              detail: `Cliente creado, pero fallaron: ${serviciosFallidos.join(
                ", "
              )}`,
              life: 5000,
            }
          : {
              severity: "success",
              summary: "Operación Exitosa",
              detail: "El cliente ha sido creado correctamente",
              life: 3000,
            }
      );

      clienteCreado = true;
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error:", error);
      toast.current.show({
        severity: "error",
        summary: "Error en la Operación",
        detail: error.message || "No se pudo crear el cliente.",
        life: 4000,
      });
    } finally {
      cliente.setLoading(false);
      if (clienteCreado) onClose();
    }
  };

  const handleGuardarCliente = async () => {
    cliente.setLoading(true);
    const serviciosFallidos = [];

    try {
      const missingFields = cliente.checkFormClientes();
      if (missingFields.length > 0) {
        toast.current.show({
          severity: "error",
          summary: "Error de Validación",
          detail:
            "Por favor, complete correctamente los siguientes campos: " +
            missingFields.join(", "),
          life: 3000,
        });
        cliente.setLoading(false);
        return;
      }

      const clienteActual = await clienteService.getClienteById(
        baseUrl,
        clienteData.id
      );

      const rutLimpio = cliente.nuevoRut.replace(/[.\s]/g, "");
      const params = new URLSearchParams({
        idCliente: clienteData.id,
        idEmpresa: cliente.nuevaEmpresa,
        idTipoCliente: cliente.nuevoTipo,
        rut: rutLimpio,
        razonSocial: cliente.nuevaRazon,
        alias: cliente.nuevoAlias,
        nombreFantasia: cliente.nuevoFanta,
        giro: cliente.giro,
        contactos: JSON.stringify(
          contacto.nuevoContactos.map((c) => ({
            idContacto: c.id,
            nombre: c.nombre,
            tipo: c.tipo,
            telefono: c.telefono,
            correo: c.correo,
          }))
        ),
        domicilios: JSON.stringify(
          direccion.nuevoDirecciones.map((d) => ({
            idDomicilio: d.id,
            direccion: d.direccion,
            tipo: d.tipo,
            comuna: d.comuna,
            region: d.region,
          }))
        ),
        representantesLegales: JSON.stringify(
          representante.nuevoRepresentantes.map((r) => ({
            idRepresentante: r.id,
            rut: r.rut,
            nombre: r.nombre,
            correo: r.correo,
            telefono: r.telefono,
          }))
        ),
        idCobranza: clienteActual.id_cobranza,
      });

      await clienteService.actualizarCliente(baseUrl, params);

      try {
        await clienteService.editarClienteCobranzas(urlCobranzas, {
          id: clienteActual.id_cobranza,
          razonSocial: cliente.nuevaRazon,
          rut: rutLimpio,
        });
      } catch {
        serviciosFallidos.push("Cobranzas");
      }

      const documentosData = {
        rut: rutLimpio,
        razonSocial: cliente.nuevaRazon,
        giro: cliente.giro,
        direccion: direccion.nuevoDirecciones[0]?.direccion || "SIN DIRECCION",
        comuna: direccion.nuevoDirecciones[0]?.comuna || "SIN COMUNA",
        telefono: contacto.nuevoContactos[0]?.telefono || "000000000",
        idCobranza: clienteActual.id_cobranza || 0,
      };
      try {
        await clienteService.actualizarClienteDocumentos(
          urlDocumentos,
          documentosData
        );
      } catch {
        serviciosFallidos.push("Documentos");
      }

      const medidoresData = {
        rut: rutLimpio,
        razonSocial: cliente.nuevaRazon,
        direccion: direccion.nuevoDirecciones[0]?.direccion || "SIN DIRECCION",
        comuna: direccion.nuevoDirecciones[0]?.comuna || "SIN COMUNA",
        telefono: contacto.nuevoContactos[0]?.telefono || "000000000",
        idCobranzas: clienteActual.id_cobranza || 0,
      };
      try {
        await clienteService.actualizarClienteMedidores(
          urlMedidores,
          medidoresData
        );
      } catch {
        serviciosFallidos.push("Medidores");
      }

      toast.current.show(
        serviciosFallidos.length > 0
          ? {
              severity: "warn",
              summary: "Actualización Parcial",
              detail: `Cliente actualizado, pero fallaron: ${serviciosFallidos.join(
                ", "
              )}`,
              life: 5000,
            }
          : {
              severity: "success",
              summary: "Operación Exitosa",
              detail: "El cliente ha sido actualizado correctamente",
              life: 3000,
            }
      );

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error:", error);
      toast.current.show({
        severity: "error",
        summary: "Error en la Operación",
        detail:
          "No se pudo actualizar el cliente. Por favor, intente nuevamente.",
        life: 3000,
      });
    } finally {
      cliente.setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="overflow-x-auto">
        <Steps
          model={stepsItems}
          activeIndex={activeStep}
          onSelect={(e) => setActiveStep(e.index)}
          className="min-w-max whitespace-nowrap cursor-pointer"
          style={{ cursor: "pointer" }}
          readOnly={false}
        />
      </div>

      {activeStep === 0 && (
        <StepClienteForm
          modoEdicion={modoEdicion}
          nuevaEmpresa={cliente.nuevaEmpresa}
          setNuevaEmpresa={cliente.setNuevaEmpresa}
          nuevoTipo={cliente.nuevoTipo}
          setNuevoTipo={cliente.setNuevoTipo}
          nuevoRut={cliente.nuevoRut}
          nuevaRazon={cliente.nuevaRazon}
          setNuevaRazon={cliente.setNuevaRazon}
          nuevoAlias={cliente.nuevoAlias}
          setNuevoAlias={cliente.setNuevoAlias}
          nuevoFanta={cliente.nuevoFanta}
          setNuevoFanta={cliente.setNuevoFanta}
          giro={cliente.giro}
          setGiro={cliente.setGiro}
          empresas={cliente.empresas}
          tipos={cliente.tipos}
          mostrarError={cliente.mostrarError}
          handleCampoTocado={cliente.handleCampoTocado}
          handleRutChange={cliente.handleRutChange}
          handleRutBlur={cliente.handleRutBlur}
          onNext={() => setActiveStep(1)}
        />
      )}

      {activeStep === 1 && (
        <StepRepresentanteForm
          modoEdicion={modoEdicion}
          nuevoNombreRepresentante={representante.nuevoNombreRepresentante}
          setNuevoNombreRepresentante={
            representante.setNuevoNombreRepresentante
          }
          nuevoRutRepresentante={representante.nuevoRutRepresentante}
          nuevoTelefonoRepresentante={representante.nuevoTelefonoRepresentante}
          nuevoCorreoRepresentante={representante.nuevoCorreoRepresentante}
          nuevoRepresentantes={representante.nuevoRepresentantes}
          codigoPais={representante.codigoPais}
          setCodigoPais={representante.setCodigoPais}
          indiceEdicionRepresentante={representante.indiceEdicionRepresentante}
          erroresRepresentante={representante.erroresRepresentante}
          codigosPaises={representante.codigosPaises}
          handleRutRepresentanteChange={
            representante.handleRutRepresentanteChange
          }
          handleRutRepresentanteBlur={representante.handleRutRepresentanteBlur}
          handleTelefonoRepresentanteChange={
            representante.handleTelefonoRepresentanteChange
          }
          handleCorreoRepresentanteChange={
            representante.handleCorreoRepresentanteChange
          }
          handleGuardarRepresentante={representante.handleGuardarRepresentante}
          handleEliminarRepresentante={
            representante.handleEliminarRepresentante
          }
          handleDesactivarRepresentante={
            representante.handleDesactivarRepresentante
          }
          handleSeleccionarRepresentante={
            representante.handleSeleccionarRepresentante
          }
          onPrev={() => setActiveStep(0)}
          onNext={() => setActiveStep(2)}
        />
      )}

      {activeStep === 2 && (
        <StepDireccionForm
          modoEdicion={modoEdicion}
          nuevaDireccion={direccion.nuevaDireccion}
          setNuevaDireccion={direccion.setNuevaDireccion}
          nuevoTipoDireccion={direccion.nuevoTipoDireccion}
          setNuevoTipoDireccion={direccion.setNuevoTipoDireccion}
          comuna={direccion.comuna}
          setComuna={direccion.setComuna}
          region={direccion.region}
          setRegion={direccion.setRegion}
          regiones={direccion.regiones}
          comunas={direccion.comunas}
          nuevoDirecciones={direccion.nuevoDirecciones}
          indiceEdicionDireccion={direccion.indiceEdicionDireccion}
          handleGuardarDireccion={direccion.handleGuardarDireccion}
          handleEliminarDireccion={direccion.handleEliminarDireccion}
          handleDesactivarDomicilio={direccion.handleDesactivarDomicilio}
          handleSeleccionarDireccion={direccion.handleSeleccionarDireccion}
          onPrev={() => setActiveStep(1)}
          onNext={() => setActiveStep(3)}
        />
      )}

      {activeStep === 3 && (
        <StepContactoForm
          modoEdicion={modoEdicion}
          nuevoContactos={contacto.nuevoContactos}
          nuevoNombreContacto={contacto.nuevoNombreContacto}
          setNuevoNombreContacto={contacto.setNuevoNombreContacto}
          nuevoTipoContacto={contacto.nuevoTipoContacto}
          setNuevoTipoContacto={contacto.setNuevoTipoContacto}
          nuevoTelefonoContacto={contacto.nuevoTelefonoContacto}
          nuevoCorreoContacto={contacto.nuevoCorreoContacto}
          codigoPaisContacto={contacto.codigoPaisContacto}
          setCodigoPaisContacto={contacto.setCodigoPaisContacto}
          indiceEdicionContacto={contacto.indiceEdicionContacto}
          codigosPaises={contacto.codigosPaises}
          handleTelefonoContactoChange={contacto.handleTelefonoContactoChange}
          handleCorreoContactoChange={contacto.handleCorreoContactoChange}
          handleGuardarContacto={contacto.handleGuardarContacto}
          handleEliminarContacto={contacto.handleEliminarContacto}
          handleDesactivarContacto={contacto.handleDesactivarContacto}
          handleSeleccionarContacto={contacto.handleSeleccionarContacto}
          onPrev={() => setActiveStep(2)}
        />
      )}

      <div className="flex justify-content-end mt-4">
        <Button
          type="button"
          label="Cancelar"
          icon="pi pi-times"
          onClick={onClose}
          className="p-button-outlined mr-2 p-button-md"
          rounded
          raised
        />
        <Button
          type="button"
          label={cliente.loading ? "Guardando..." : "Guardar"}
          icon={cliente.loading ? "pi pi-spin pi-spinner" : "pi pi-save"}
          onClick={confirmGuardar}
          className="p-button p-button-md"
          severity="success"
          rounded
          autoFocus
          loading={cliente.loading}
          disabled={cliente.loading}
          raised
        />
      </div>
    </>
  );
};

export default FormCrearCliente;
